import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Button, Skeleton, Text } from '../../../../components';
import type { Player } from '../../../../mock-data/types';
import { openInApp } from '../../../creatives/components/LandingPageComposer/link-for';
import type { CameFrom } from '../../attribution';
import { netAfterCommission, type CommissionView, type FixedFee } from '../../commission-estimate';
import { money, playerLabel, prettyDate, prettyDateTime } from '../../format';
import { channelOf, sourceOf } from '../../roster-view';
import { useSourceLabel } from '../../useSourceLabel';
import { PlayerAvatar } from '../PlayerAvatar';
import { EstimateInfo } from '../EstimateInfo';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import {
  Content,
  Hero,
  Identity,
  Name,
  EmailRow,
  EmailText,
  CopyBtn,
  Tiles,
  Tile,
  TileLabel,
  TileValue,
  TileNote,
  TileNoteAmount,
  TileNoteInfo,
  Group,
  GroupHead,
  GroupHeadLabel,
  GroupLabel,
  EstimateValue,
  NoValue,
  MetaList,
  MetaRow,
  MetaLabel,
  MetaValue,
  CameValue,
  SheetLink,
  ViaLine,
  ViaLink,
} from './PlayerDetailSheet.styles';

export type PlayerDetailSheetProps = {
  player: Player;
  onClose: () => void;
  /** Copy the email to the clipboard (drives the page toast). */
  onCopyEmail: (email: string) => void;
  /** Where the player came from (see `attribution.ts`). */
  cameFrom: CameFrom;
  /** What the affiliate who brought the player earns for them, estimated (see `commission-estimate.ts`). */
  commission: CommissionView;
  /** The brand the roster belongs to (every player on the page is on it). */
  brandName?: string;
  /** Open an in-app path (a "came from" deep link). The page closes the sheet first, then opens it (an asset in place). */
  onOpenPath: (path: string) => void;
};

/** One label/value row, omitted entirely when the field is absent for this player. */
const Row = ({ label, value }: { label: string; value: string | undefined }): React.ReactElement | null =>
  value === undefined ? null : (
    <MetaRow>
      <MetaLabel text={label} tag="span" />
      <MetaValue text={value} tag="span" />
    </MetaRow>
  );

/** Stands in for the amount while the "after commission" sentence is translated, to find where it goes. */
const AMOUNT_MARK = '\u2063AMOUNT\u2063';

/** The sign of what the brand keeps after commission, for its colour: of the amount as shown (whole dollars), so a
 *  loss that rounds to "$0" doesn't read red. */
const afterCommissionTone = (value: number): 'gain' | 'loss' | 'even' => {
  const shown = Math.round(value);
  return shown > 0 ? 'gain' : shown < 0 ? 'loss' : 'even';
};

/** A commission row's value, already translated: plain, an estimate (muted ink), nothing to say ("N/A" / "–"), or
 *  still loading. */
type CellValue = { text: string; estimate?: boolean; empty?: boolean } | 'pending';

/** A row that always renders, so every player's commission group has the same rows in the same order. */
const ValueRow = ({ label, value }: { label: string; value: CellValue }): React.ReactElement => (
  <MetaRow>
    <MetaLabel text={label} tag="span" />
    {value === 'pending' ? (
      <Skeleton width={64} height={13} radius={5} />
    ) : value.empty ? (
      <NoValue raw text={value.text} tag="span" />
    ) : value.estimate ? (
      <EstimateValue raw text={value.text} tag="span" />
    ) : (
      <MetaValue raw text={value.text} tag="span" />
    )}
  </MetaRow>
);

/**
 * Player detail — rendered inside the shared <Sheet> (a centred modal on
 * desktop, a bottom sheet on mobile; Overlay owns the portal, backdrop-click,
 * Escape and background scroll-lock).
 *
 * The body is the player's full record across BOTH data sets: the Account group is
 * the registration row (`select`/`players`) and the Activity group the transaction
 * figures (`daily_player_activity`).
 *
 * The two groups treat absence differently, on purpose. An ACCOUNT row the API did
 * not return is omitted rather than shown blank, so nothing is asserted about a
 * field nobody captured. The ACTIVITY figures are always rendered, because a player
 * with no activity row has genuinely transacted zero times: there the zero is the
 * answer, not a gap. The headline tiles are deposits and NGR: the money in, and what the brand keeps of it.
 */
export const PlayerDetailSheet = ({
  player,
  onClose,
  onCopyEmail,
  cameFrom,
  commission,
  brandName,
  onOpenPath,
}: PlayerDetailSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  // Dismiss through the overlay so the dialog slides out; wired straight to onClose it
  // would unmount on the spot and blink away.
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const sourceLabel = useSourceLabel();
  const source = sourceOf(player);
  const label = playerLabel(player, t);

  const yesNo = (value: boolean | undefined): string | undefined =>
    value === undefined ? undefined : value ? t('Yes') : t('No');

  // "Came from": the asset (and the landing page it went through) as deep links; a plain value otherwise. A plain
  // click opens it in the app (the page closes the sheet on the way); a modified one opens a new tab.
  const cameValue = ((): React.ReactNode => {
    switch (cameFrom.kind) {
      case 'link': {
        const label = `${cameFrom.kindLabel} · ${cameFrom.name}`;
        const { path, via } = cameFrom;
        const viaPath = via?.path;
        return (
          <CameValue>
            {path ? (
              <SheetLink href={path} title={label} onClick={(e) => openInApp(e, path, onOpenPath)}>
                <Text raw text={label} tag="span" />
              </SheetLink>
            ) : (
              <MetaValue raw text={label} tag="span" />
            )}
            {via ? (
              <ViaLine>
                <Text text="via" tag="span" />
                {viaPath ? (
                  <ViaLink href={viaPath} title={via.name} onClick={(e) => openInApp(e, viaPath, onOpenPath)}>
                    <Text raw text={via.name} tag="span" />
                  </ViaLink>
                ) : (
                  <Text raw text={via.name} tag="span" />
                )}
              </ViaLine>
            ) : null}
          </CameValue>
        );
      }
      case 'affiliate': {
        const { path, named } = cameFrom;
        const label = named ? `${t('Affiliate')} · ${named.affiliate}` : cameFrom.label;
        return (
          <CameValue>
            <SheetLink href={path} title={label} onClick={(e) => openInApp(e, path, onOpenPath)}>
              <Text raw text={label} tag="span" />
            </SheetLink>
            {named ? (
              <ViaLine>
                <Text text="Campaign" tag="span" />
                <Text raw text={named.campaign} tag="span" />
              </ViaLine>
            ) : null}
          </CameValue>
        );
      }
      case 'pending':
        return <Skeleton width={120} height={13} radius={5} />;
      case 'organic':
      case 'direct':
      case 'other':
        return <MetaValue raw text={cameFrom.label} tag="span" />;
      default:
        return null;
    }
  })();

  // First deposit: its date when the API filled `ftd`, else whether the player has deposited at all.
  const firstDeposit =
    player.ftdAt !== undefined ? prettyDate(player.ftdAt) : player.depositsCount > 0 ? t('Yes') : t('No');
  // Under the NGR tile, what the brand keeps once the affiliate is paid: only when a commission is actually due.
  const afterCommissionRaw = netAfterCommission(player, commission);
  const commissionDue = commission.kind === 'estimate' && (commission.estimate?.total ?? 0) !== 0;
  const afterCommission = commissionDue ? afterCommissionRaw : undefined;
  // The sentence is translated whole, then cut at the amount, so only the amount is coloured and each language keeps
  // its own word order.
  const [noteBefore = '', noteAfter = ''] = t('{{amount}} after commission', { amount: AMOUNT_MARK }).split(AMOUNT_MARK);
  // A bonus or revenue figure the activity row didn't carry reads "–", never a zero nobody measured.
  const revenue = (value: number | undefined): string => (value === undefined ? '–' : money(value));

  // The commission group has the same seven rows for every player (Plan, CPA, CPA commission, Rev share, Rev share
  // commission, Fixed fee, Total commission). The fixed fee is the plan's terms, never split per player, so the
  // total (and the "after commission" line) leave it out:
  // "N/A" where a part doesn't apply (not an affiliate player, or the plan doesn't pay it), "–" where it does but
  // can't be read, "≈" on an estimate.
  // "500 EUR / month" (", prorated" when a part month is): the amount in the plan's own currency, as set.
  const feeTerms = (fee: FixedFee): string => {
    const amount = `${fee.amount.toLocaleString('en-US')}${fee.currency ? ` ${fee.currency}` : ''}`;
    return fee.prorated ? t('{{amount}} / month, prorated', { amount }) : t('{{amount}} / month', { amount });
  };
  const NA: CellValue = { text: t('N/A'), empty: true };
  const DASH: CellValue = { text: '–', empty: true };
  const estimated = (value: number | undefined): CellValue =>
    value === undefined ? DASH : { text: `≈${money(value)}`, estimate: true };
  const commissionCells = ((): CellValue[] => {
    switch (commission.kind) {
      case 'pending':
        return ['pending', 'pending', 'pending', 'pending', 'pending', 'pending', 'pending'];
      case 'none':
        return [NA, NA, NA, NA, NA, NA, NA];
      case 'no-plan':
        return [{ text: t('None') }, NA, NA, NA, NA, NA, NA];
      case 'estimate': {
        const { estimate, paysCpa, paysRevshare, revsharePercent, tiered, fixedFee } = commission;
        const qualified = estimate?.cpaQualified;
        // The plan's rate, as set (not an estimate); a tiered plan's first tier is the one the estimate used.
        const rate: CellValue = !paysRevshare
          ? NA
          : revsharePercent === undefined
            ? DASH
            : { text: tiered ? t('Tiered, from {{percent}}%', { percent: revsharePercent }) : `${revsharePercent}%` };
        return [
          { text: commission.planName },
          !paysCpa
            ? NA
            : qualified === undefined
              ? DASH
              : { text: qualified ? t('Qualified') : t('Not qualified'), estimate: true },
          paysCpa ? estimated(estimate?.cpa) : NA,
          rate,
          paysRevshare ? estimated(estimate?.revshare) : NA,
          fixedFee === undefined ? NA : { text: feeTerms(fixedFee) },
          estimate === undefined ? NA : estimated(estimate.total),
        ];
      }
      default:
        return [DASH, DASH, DASH, DASH, DASH, DASH, DASH];
    }
  })();
  const [planCell, cpaCell, cpaCommissionCell, rateCell, revshareCell, feeCell, totalCell] = commissionCells;
  const hasFixedFee = commission.kind === 'estimate' && commission.fixedFee !== undefined;

  // Every Account row is optional, and a player can reach this sheet with no
  // registration row at all (an activity-only merge row). Each `Row` then renders
  // nothing, which would leave a labelled ACCOUNT heading standing over an empty
  // list, so the whole group goes when it has nothing to say. The brand always has
  // something to say (every player on the page is on it), so with a brand the group
  // shows at least that row.
  const hasAccountDetails =
    brandName !== undefined ||
    player.playerId !== undefined ||
    player.registeredAt !== undefined ||
    player.lastLoginAt !== undefined ||
    player.country !== undefined ||
    player.source !== undefined ||
    player.campaign !== undefined ||
    player.emailVerified !== undefined;

  return (
    <Sheet
      title="{{name}}"
      titlePlaceholders={{ name: player.name ?? label }}
      ariaLabel={t('Player details')}
      onClose={onClose}
      closeHandle={closeHandle}
      footer={<Button label={t('Close')} variant="outline" fullWidth onClick={dismiss} />}
    >
      <Content>
        <Hero>
          <PlayerAvatar email={player.email || player.id} size={64} />
          <Identity>
            {player.name !== undefined ? <Name text={player.name} tag="span" /> : null}
            {player.email !== '' ? (
              <EmailRow>
                <EmailText text={player.email} tag="span" title={player.email} />
                <CopyBtn type="button" aria-label={t('Copy email')} onClick={() => onCopyEmail(player.email)}>
                  <Icon name="copy" size={13} />
                </CopyBtn>
              </EmailRow>
            ) : player.name === undefined ? (
              <Name text={label} tag="span" />
            ) : null}
          </Identity>
        </Hero>

        <Tiles>
          <Tile>
            <TileLabel text="Deposits" tag="span" />
            <TileValue text={money(player.deposits, true)} tag="span" />
          </Tile>
          <Tile>
            <TileLabel text="Net gaming revenue" tag="span" />
            <TileValue text={revenue(player.ngr)} tag="span" $accent />
            {afterCommission !== undefined ? (
              <TileNote>
                <Text raw text={noteBefore} tag="span" />
                <TileNoteAmount
                  raw
                  text={`≈${money(afterCommission)}`}
                  tag="span"
                  $tone={afterCommissionTone(afterCommission)}
                />
                <Text raw text={noteAfter} tag="span" />
                <TileNoteInfo>
                  <EstimateInfo align="end" fixedFee={hasFixedFee} />
                </TileNoteInfo>
              </TileNote>
            ) : null}
          </Tile>
        </Tiles>

        {hasAccountDetails ? (
          <Group>
            <GroupLabel text="Account" tag="span" />
            <MetaList>
              <Row label={t('Brand')} value={brandName} />
              <Row label={t('Player ID')} value={player.playerId} />
              <Row
                label={t('Registered')}
                value={player.registeredAt !== undefined ? prettyDateTime(player.registeredAt) : undefined}
              />
              {/* "Never" is a real answer here, not missing data: the API reports it
                  as an epoch sentinel, which the roster maps to `undefined`. It is
                  only sayable when this player HAS a registration row (same marker
                  the Registered row above uses): an activity-only row carries no
                  login field, and asserting "never signed in" about a player who
                  demonstrably wagered would be inventing the answer. */}
              <Row
                label={t('Last signed in')}
                value={
                  player.lastLoginAt !== undefined
                    ? prettyDateTime(player.lastLoginAt)
                    : player.registeredAt !== undefined
                      ? t('Never')
                      : undefined
                }
              />
              <Row label={t('Country')} value={player.country} />
              <Row
                label={t('Acquisition source')}
                value={source !== undefined ? sourceLabel(channelOf(source)) : undefined}
              />
              {cameValue !== null ? (
                <MetaRow>
                  <MetaLabel text="Came from" tag="span" />
                  {cameValue}
                </MetaRow>
              ) : null}
              <Row label={t('Email verified')} value={yesNo(player.emailVerified)} />
            </MetaList>
          </Group>
        ) : null}

        <Group>
          <GroupLabel text="Activity" tag="span" />
          <MetaList>
            <Row label={t('First deposit')} value={firstDeposit} />
            <Row label={t('Number of deposits')} value={player.depositsCount.toLocaleString('en-US')} />
            <Row label={t('Bets placed')} value={player.bets.toLocaleString('en-US')} />
          </MetaList>
        </Group>

        <Group>
          <GroupLabel text="Cash" tag="span" />
          <MetaList>
            <Row label={t('Deposits')} value={money(player.deposits, true)} />
            <Row label={t('Withdrawals')} value={money(player.withdrawals, true)} />
            <Row label={t('Net cash')} value={money(player.deposits - player.withdrawals, true)} />
          </MetaList>
        </Group>

        <Group>
          <GroupLabel text="Revenue" tag="span" />
          <MetaList>
            <Row label={t('Bet amount')} value={money(player.wagered)} />
            <Row label={t('Win amount')} value={money(player.wins)} />
            <Row label={t('GGR')} value={money(player.ggr)} />
            <Row label={t('Bonus awarded')} value={revenue(player.bonusAwarded)} />
            <Row label={t('Bonus GGR')} value={revenue(player.bonusGgr)} />
            <Row label={t('Bonus transformed')} value={revenue(player.bonusTransformed)} />
            <Row label={t('Bonus cost')} value={revenue(player.bonusTransformed)} />
            <Row label={t('NGR')} value={revenue(player.ngr)} />
          </MetaList>
        </Group>

        <Group>
          <GroupHead>
            <GroupHeadLabel text="Affiliate commission" tag="span" />
            {commission.kind === 'estimate' ? <EstimateInfo fixedFee={hasFixedFee} /> : null}
          </GroupHead>
          <MetaList>
            <ValueRow label={t('Plan')} value={planCell} />
            <ValueRow label={t('CPA')} value={cpaCell} />
            <ValueRow label={t('CPA commission')} value={cpaCommissionCell} />
            <ValueRow label={t('Rev share')} value={rateCell} />
            <ValueRow label={t('Rev share commission')} value={revshareCell} />
            <ValueRow label={t('Fixed fee')} value={feeCell} />
            <ValueRow label={t('Total commission')} value={totalCell} />
          </MetaList>
        </Group>
      </Content>
    </Sheet>
  );
};

export default PlayerDetailSheet;
